import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight, KeyRound, Mail } from 'lucide-react';
import { APP } from '@/constants/testIds';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const formatErr = (d) => {
  if (!d) return 'Something went wrong';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return d?.msg ? d.msg : String(d);
};

const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Email and password required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.session_token) localStorage.setItem('sws_token', data.session_token);
      setUser?.(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      const dest = data.user.role === 'admin' ? '/admin' : '/app';
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 py-10">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute inset-0 glow-radial" />
      <div className="relative w-full max-w-md panel p-10" data-testid={APP.loginPage}>
        <Link to="/" className="flex items-center gap-3 mb-10">
          <span className="logo-mark">S</span>
          <span className="font-mono text-xs tracking-[0.28em] uppercase">Supreme<span className="text-[color:var(--brand)]">/</span>Web Security</span>
        </Link>
        <div className="label-tag mb-3">// Secure Sign-in</div>
        <h1 className="text-3xl font-light tracking-tight mb-3">Access your security console</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mb-8 leading-relaxed">
          Sign in with Google, or use your account credentials below.
        </p>

        <button
          data-testid={APP.loginGoogleBtn}
          onClick={handleGoogleLogin}
          className="btn-primary w-full justify-center mb-6"
        >
          <ShieldCheck size={14} /> Continue with Google
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-[color:var(--border)]" />
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--text-muted)]">or use credentials</span>
          <div className="flex-1 h-px bg-[color:var(--border)]" />
        </div>

        <form onSubmit={handlePasswordLogin} className="space-y-4 mt-5" data-testid="password-login-form">
          <div>
            <div className="label-tag mb-2 flex items-center gap-2"><Mail size={12} /> Email</div>
            <input
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-base"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <div className="label-tag mb-2 flex items-center gap-2"><KeyRound size={12} /> Password</div>
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-base"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            <ShieldCheck size={14} /> {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="glow-divider my-8" />
        <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--text-muted)] flex items-center justify-between">
          <span>Secure Sign-in</span>
          <Link to="/" className="hover:text-white inline-flex items-center gap-1">Back <ArrowRight size={12} /></Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
